import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import useResizeObserver from '@hooks/useResizeObserver'
import Video from '@components/atoms/video'
import { PauseButton, PlayButton } from '@components/atoms/icons'
import Typography from '@material-ui/core/Typography'
import RendererRichText from '@components/renderers/render-rich-text'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import { useStyles } from './custom-hero.styles'
import QuickLinks from './quick-links'

const CustomHero = (props) => {
  const {
    heroBackground,
    heroHeadline,
    heroEyebrow,
    heroSummary,
    showHeroBackgroundOverlay,
    primaryCtaText,
    primaryCtaUrl,
    secondaryCtaText,
    secondaryCtaUrl,
    quickLinks,
  } = props
  const [ref, entry] = useResizeObserver()
  const [offset, setOffset] = useState(0)

  const videoBackground = heroBackground
    && heroBackground.url.includes('video')
    && heroBackground.url

  const images = {
    sm: heroBackground
      ? `${heroBackground.url}?fm=jpg&fl=progressive&q=80`
      : '',
    lg: heroBackground
      ? `${heroBackground.url}?fm=jpg&h=1200&w=1800&fit=fill&fl=progressive&q=80`
      : '',
  }

  const styles = useStyles({ images, offset, showHeroBackgroundOverlay })

  const videoRef = useRef()

  const handlePause = () => {
    videoRef.current.pause()
  }
  const handlePlay = () => {
    videoRef.current.play()
  }

  useEffect(() => {
    if (entry?.contentRect?.height) {
      setOffset(entry.contentRect.height / 2)
    }
  }, [entry?.contentRect?.height])

  return (
    <Box
      className={styles.root}
      display="flex"
      flexDirection="column"
    >
      {videoBackground ? (
        <>
          <Video
            src={videoBackground}
            autoPlay
            muted
            loop
            playsInline
            className={styles.videoContainer}
            ref={videoRef}
            data-testid="custom-hero-video"
          />
          <div className={styles.buttonContainer}>
            <button
              title="Pause background video"
              type="button"
              className={styles.videoButton}
              onClick={handlePause}
            >
              <PauseButton />
            </button>
            <button
              title="Play background video"
              type="button"
              className={styles.videoButton}
              onClick={handlePlay}
            >
              <PlayButton />
            </button>
          </div>
        </>
      ) : (
        <Box className={styles.imageContainer}>
          <div className={styles.overlay}>
            <Container className={styles.heroContent}>
              <Typography variant="h5">{heroEyebrow}</Typography>
              <Typography variant="h2">{heroHeadline}</Typography>
              <Box className={styles.heroSummary}>
                <RendererRichText {...heroSummary} />
              </Box>
              <Box display="flex" justifyContent="center" className={styles.ctaContainer}>
                {primaryCtaText && primaryCtaUrl && (
                  <Button variant="contained" color="primary" href={primaryCtaUrl}>
                    {primaryCtaText}
                  </Button>
                )}
                {secondaryCtaText && secondaryCtaUrl && (
                  <Button variant="text" href={secondaryCtaUrl} style={{ color: '#E6BC5A' }}>
                    {secondaryCtaText}
                  </Button>
                )}
              </Box>
            </Container>
          </div>
          <QuickLinks {...quickLinks} ref={ref} offset={offset} />
        </Box>
      )}
    </Box>
  )
}

CustomHero.propTypes = {
  heroBackground: PropTypes.shape({
    url: PropTypes.string,
  }),
  heroHeadline: PropTypes.string,
  heroEyebrow: PropTypes.string,
  heroSummary: PropTypes.object,
  showHeroBackgroundOverlay: PropTypes.bool,
  primaryCtaText: PropTypes.string,
  primaryCtaUrl: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaUrl: PropTypes.string,
  quickLinks: PropTypes.object,
}

export default CustomHero
